import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { confirmSignUp, resendSignUpCode, autoSignIn } from "aws-amplify/auth";
import { toast } from "sonner";
import logoLockup from "@/assets/logo-lockup-color.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function ConfirmSignUpPage() {
  usePageTitle('Confirm Your Email | BookMyCoaching');
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email ?? "";

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
        <div className="w-full max-w-md text-center">
          <p className="text-muted-foreground mb-4">
            We couldn't find an in-progress registration. Please sign up again.
          </p>
          <Link to="/register" className="font-medium text-foreground underline">
            Back to Sign Up
          </Link>
        </div>
      </div>
    );
  }

  const finishSignIn = async () => {
    try {
      await autoSignIn();
    } catch {
      // Auto sign-in wasn't available (e.g. cancelled/expired) — fall back
      // to sending the user to Login rather than leaving them stuck.
      toast.success("Registered! Please log in.");
      navigate("/login");
      return;
    }
    navigate("/auth/callback");
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) {
      setError("Enter the code we emailed you");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { nextStep } = await confirmSignUp({ username: email, confirmationCode: code.trim() });
      if (nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        await finishSignIn();
        return;
      }
      toast.success("Registered! Please log in.");
      navigate("/login");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Invalid or expired code";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendSignUpCode({ username: email });
      toast.success("Code resent — check your email");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not resend code";
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex justify-center group">
          <img
            src={logoLockup}
            alt="BookMyCoaching"
            className="h-10 sm:h-14 w-auto max-w-full group-hover:opacity-90 transition-opacity"
          />
        </Link>

        <div className="mb-8 text-center">
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-foreground mb-2 leading-snug">
            Confirm your email
          </h1>
          <p className="text-muted-foreground text-sm">
            We sent a code to <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        <form onSubmit={handleConfirm} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="code" className="text-sm font-medium">Confirmation code</Label>
            <Input
              id="code"
              value={code}
              onChange={e => setCode(e.target.value)}
              placeholder="123456"
              inputMode="numeric"
              aria-invalid={!!error}
              className={cn("h-14 text-center text-3xl font-bold tracking-[0.4em]", error && "border-destructive focus-visible:ring-destructive")}
              aria-live="polite"
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full font-semibold">
            {submitting ? "Confirming…" : "Confirm"}
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-center gap-4 text-sm">
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-foreground underline hover:no-underline disabled:opacity-50"
          >
            {resending ? "Resending…" : "Resend code"}
          </button>
          <span className="text-muted-foreground">·</span>
          <Link to="/login" className="text-foreground underline hover:no-underline">
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
